import { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  CookingPot,
  Layers,
  Package,
  PhoneCall,
  RefreshCw,
  Truck,
  XCircle,
  type LucideIcon,
} from 'lucide-react'
import { useGetLastOrderImport } from '../../hooks/backend/scanPage'
import { useGetProductionPlan } from '../../hooks/backend/recipes'
import { useGetReorderReminders } from '../../hooks/backend/crm'
import { useGetAllOrderLinesCached } from '../../hooks/cachedQueries'
import { useHubAlerts } from '../../hooks/useHubAlerts'
import { Button } from '../../lib/shadcn/button'
import { cn } from '../../lib/shadcn/utils'
import { bags } from '../../utils/bagStatus'
import { ORDERS_IMPORTED_EVENT, timeAgo, type LastOrderImport } from '../../utils/orderImport'
import SeasonalAlertList from './SeasonalAlertList'
import InvoiceProblemsAlert from './InvoiceProblemsAlert'

interface OrderLine {
  order_id: string
  quantity_ordered: number
  quantity_printed: number
  quantity_picked: number
  order_status?: string | null
  delivery_method?: string | null
}

interface PlanFlavorSummary {
  units_to_make: number
  batches: number | null
  recipe_only: boolean
  blend_by_percent: boolean
}

interface ReminderSummary {
  status: 'overdue' | 'lapsed' | 'one_time'
}

export interface OrderCounts {
  toPick: number
  notStarted: number
  inProgress: number
  toShip: number
  toDeliver: number
  bagsToPick: number
}

// Same rules as the Orders page (picked vs ordered, or Zoho/Hub status) and
// the Shipping page (delivery method, shipped/delivered status).
export function countOrders(lines: OrderLine[]): OrderCounts {
  const orders = new Map<
    string,
    { total: number; printed: number; picked: number; status: string; shipping: boolean }
  >()
  for (const line of lines) {
    const key = String(line.order_id)
    const o = orders.get(key) ?? {
      total: 0,
      printed: 0,
      picked: 0,
      status: String(line.order_status ?? '').toLowerCase(),
      shipping: (line.delivery_method ?? 'shipping') === 'shipping',
    }
    o.total += Number(line.quantity_ordered ?? 0)
    o.printed += Number(line.quantity_printed ?? 0)
    o.picked += Number(line.quantity_picked ?? 0)
    orders.set(key, o)
  }

  const counts: OrderCounts = { toPick: 0, notStarted: 0, inProgress: 0, toShip: 0, toDeliver: 0, bagsToPick: 0 }
  for (const o of orders.values()) {
    if (o.status === 'shipped' || o.status === 'delivered' || o.status === 'void') continue
    const complete = o.status === 'complete' || (o.total > 0 && o.picked >= o.total)
    if (!complete) {
      counts.toPick++
      counts.bagsToPick += Math.max(0, o.total - o.picked)
      if (o.picked > 0) counts.inProgress++
      else if (!(o.total > 0 && o.printed >= o.total)) counts.notStarted++
    } else if (o.shipping) {
      counts.toShip++
    } else {
      counts.toDeliver++
    }
  }
  return counts
}

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`

function Tile({
  to,
  icon: Icon,
  label,
  count,
  detail,
  loading,
}: {
  to: string
  icon: LucideIcon
  label: string
  count: number | null
  detail: string
  loading: boolean
}) {
  const idle = count === 0
  return (
    <Link
      to={to}
      className={cn(
        'group rounded-lg border bg-card p-4 flex flex-col gap-1 shadow-sm transition-colors hover:bg-accent/60'
      )}
    >
      <span className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Icon className="h-4 w-4" />
        {label}
      </span>
      <span className={cn('text-3xl font-bold tabular-nums', idle && 'text-muted-foreground')}>
        {loading || count === null ? '–' : count.toLocaleString('en-US')}
      </span>
      <span className="text-xs text-muted-foreground min-h-4">{loading ? 'Loading…' : detail}</span>
    </Link>
  )
}

// Top of the Dashboard: what needs doing today, with links to the page where
// it gets done, plus the alerts that used to show as banners on every page.
export default function TodayPanel() {
  const getLines = useGetAllOrderLinesCached()
  const getPlan = useGetProductionPlan()
  const getReminders = useGetReorderReminders()
  const getLastImport = useGetLastOrderImport()
  const alerts = useHubAlerts()

  const load = (skipCache = false) => {
    void getLines.trigger(undefined, { skipCache })
    void getPlan.trigger({ includeForecast: true }, { skipCache })
    void getReminders.trigger({}, { skipCache })
    void getLastImport.trigger({}, { skipCache })
  }

  useEffect(() => {
    load()
    const onImported = () => {
      void getLines.trigger(undefined, { skipCache: true })
      void getPlan.trigger({ includeForecast: true }, { skipCache: true })
      void getLastImport.trigger({}, { skipCache: true })
    }
    window.addEventListener(ORDERS_IMPORTED_EVENT, onImported)
    return () => window.removeEventListener(ORDERS_IMPORTED_EVENT, onImported)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const lines = getLines.data as OrderLine[] | null | undefined
  const orders = useMemo(() => (lines ? countOrders(lines) : null), [lines])

  const plan = getPlan.data as { flavors?: PlanFlavorSummary[] } | null | undefined
  const toMake = useMemo(() => {
    if (!plan?.flavors) return null
    // Percentage blends are mixed from other flavors, not cooked on their own.
    const cooked = plan.flavors.filter(f => !f.blend_by_percent && (f.batches ?? 0) > 0)
    return {
      flavors: cooked.length,
      batches: cooked.reduce((sum, f) => sum + (f.batches ?? 0), 0),
    }
  }, [plan])

  const reminders = getReminders.data as ReminderSummary[] | null | undefined
  const overdue = reminders ? reminders.filter(r => r.status === 'overdue').length : null

  const lastImport = getLastImport.data as LastOrderImport | null | undefined
  const importFailed = !!lastImport?.lastRun?.error

  const refreshing = getLines.loading || getPlan.loading || getReminders.loading || getLastImport.loading
  const alertCount = alerts.count + (importFailed ? 1 : 0)

  const pickDetail = orders
    ? orders.toPick === 0
      ? 'All orders picked'
      : [
          plural(orders.bagsToPick, 'bag'),
          orders.notStarted > 0 ? `${orders.notStarted} not started` : null,
          orders.inProgress > 0 ? `${orders.inProgress} in progress` : null,
        ]
          .filter(Boolean)
          .join(' · ')
    : ''

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Today</h2>
          <p className="text-sm text-muted-foreground">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => load(true)} disabled={refreshing}>
          <RefreshCw className={cn('w-4 h-4 mr-2', refreshing && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Tile
          to="/orders"
          icon={ClipboardList}
          label="To pick"
          count={orders?.toPick ?? null}
          loading={!orders && getLines.loading}
          detail={pickDetail}
        />
        <Tile
          to="/shipping"
          icon={Package}
          label="Ready to ship"
          count={orders?.toShip ?? null}
          loading={!orders && getLines.loading}
          detail={orders ? (orders.toShip === 0 ? 'Nothing waiting' : 'Picked, not shipped') : ''}
        />
        <Tile
          to="/shipping?tab=delivery"
          icon={Truck}
          label="Ready to deliver"
          count={orders?.toDeliver ?? null}
          loading={!orders && getLines.loading}
          detail={orders ? (orders.toDeliver === 0 ? 'Nothing waiting' : 'Picked, not delivered') : ''}
        />
        <Tile
          to="/production"
          icon={CookingPot}
          label="To make"
          count={toMake?.flavors ?? null}
          loading={!toMake && getPlan.loading}
          detail={
            toMake
              ? toMake.flavors === 0
                ? 'Nothing in the plan'
                : `${plural(toMake.flavors, 'flavor')} · ${plural(toMake.batches, 'batch', 'batches')}`
              : getPlan.error
                ? "Couldn't load the plan"
                : ''
          }
        />
        <Tile
          to="/customers?tab=reorders"
          icon={PhoneCall}
          label="Reorders due"
          count={overdue}
          loading={reminders == null && getReminders.loading}
          detail={overdue == null ? '' : overdue === 0 ? 'Everyone on pace' : 'Customers past their usual pace'}
        />
      </div>

      <div className="rounded-lg border bg-card px-4 py-1">
        {alertCount === 0 ? (
          <p className="flex items-center gap-2 py-2.5 text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            {alerts.loading ? 'Checking for alerts…' : 'No alerts. Bags are stocked and orders are importing.'}
          </p>
        ) : (
          <ul className="divide-y">
            {importFailed && lastImport?.lastRun && (
              <li className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2">
                <XCircle className="h-4 w-4 shrink-0 text-destructive" />
                <span className="flex-1 min-w-0 text-sm">
                  The last Zoho order import failed {timeAgo(lastImport.lastRun.started_at)}
                  {lastImport.lastSuccess
                    ? `; orders were last imported ${timeAgo(lastImport.lastSuccess.started_at)}`
                    : ''}
                  . <span className="text-muted-foreground">{lastImport.lastRun.error}</span>
                </span>
                <Link to="/orders" className="text-sm font-medium underline underline-offset-2 whitespace-nowrap max-sm:pl-7">
                  Go to Orders
                </Link>
              </li>
            )}
            {alerts.lowBags.length > 0 && (
              <li className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2">
                <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />
                <span className="flex-1 min-w-0 text-sm">
                  Time to reorder bags:{' '}
                  {alerts.lowBags
                    .map(
                      s =>
                        `${s.size} has ${bags(Math.max(0, s.on_hand))} left (reorder at ${s.reorder_point.toLocaleString('en-US')})`
                    )
                    .join('; ')}
                  .
                </span>
                <Link
                  to="/settings?section=bags"
                  className="text-sm font-medium underline underline-offset-2 whitespace-nowrap max-sm:pl-7"
                >
                  View bags
                </Link>
              </li>
            )}
            <InvoiceProblemsAlert problems={alerts.invoiceProblems} />
            <SeasonalAlertList alerts={alerts.seasonal} />
            {alerts.tierReview && (
              <li className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2">
                <Layers className="h-4 w-4 shrink-0 text-primary" />
                <span className="flex-1 min-w-0 text-sm">
                  <span className="font-medium">Tier review ready:</span>{' '}
                  {plural(alerts.tierReview.pending, 'wholesale tier change')} suggested from{' '}
                  {alerts.tierReview.quarter} sales.
                </span>
                <Link
                  to="/tier-review"
                  className="text-sm font-medium underline underline-offset-2 whitespace-nowrap max-sm:pl-7"
                >
                  Review tiers
                </Link>
              </li>
            )}
          </ul>
        )}
      </div>
    </section>
  )
}
